import { useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { wanneer } from "./RepoVastleggingen";

/**
 * EG9 (GO Frank 1 november 2026): een spiegel van GitHub. GitHub is de bron; NovaMund volgt met Ververs vanaf GitHub
 * en is er alleen te lezen. Losmaken (na een tweede klik) maakt NovaMund de bron; terug naar spiegelen kan niet.
 */
export default function RepoSpiegel({ repo, onVeranderd }) {
  const { t, taal } = useTaal();
  const [bezig, setBezig] = useState("");
  const [losVraag, setLosVraag] = useState(false);
  // G1: een privé-spiegel vraagt bij elke Ververs het leestoken; het blijft alleen in dit veld tot het verzoek weg is.
  const [token, setToken] = useState("");
  const bron = repo.bron || {};

  async function doe(soort) {
    if (soort === "los" && !losVraag) return setLosVraag(true);
    setLosVraag(false);
    setBezig(soort);
    try {
      const lijf = soort === "ververs" && bron.prive ? [{ token: token.trim() }] : [];
      const { data } = await axios.post(`${API}/repos/${repo.id}/spiegel/${soort}`, ...lijf);
      if (soort === "ververs") {
        const n = data.bijgewerkt.length + data.nieuw.length + data.weg.length;
        toast.success(n ? t("Ververst: {b} bijgewerkt, {n} nieuw, {w} weg.", { b: data.bijgewerkt.length, n: data.nieuw.length, w: data.weg.length })
          : t("Ververst: alles was al gelijk aan GitHub."));
        onVeranderd?.(data.repo);
      } else {
        toast.success(t("Losgemaakt van GitHub: NovaMund is nu de bron."));
        onVeranderd?.(data);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Dat lukte niet."));
    } finally {
      setToken("");
      setBezig("");
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/5 p-2 text-xs" data-testid="repo-spiegel">
      <span className="min-w-0 flex-1 break-words">
        {t("Spiegel van {adres}: GitHub is de bron, hier alleen te lezen.", { adres: bron.adres })}
        {bron.gespiegeld_op && <> {t("Laatst ververst {wanneer}.", { wanneer: wanneer(new Date(bron.gespiegeld_op * 1000).toISOString(), taal) })}</>}
      </span>
      {bron.prive && (
        <input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)}
               placeholder={t("Leestoken van GitHub")} aria-label={t("Leestoken van GitHub")}
               className="min-w-0 rounded-md border bg-transparent px-2 py-1" data-testid="spiegel-token" />
      )}
      <Button type="button" size="sm" variant="outline" className="tap-target"
              disabled={Boolean(bezig) || (bron.prive && !token.trim())} onClick={() => doe("ververs")}
              data-testid="spiegel-ververs">
        {bezig === "ververs" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
        {t("Ververs vanaf GitHub")}
      </Button>
      <Button type="button" size="sm" variant={losVraag ? "destructive" : "ghost"} className="tap-target" disabled={Boolean(bezig)}
              onClick={() => doe("los")} data-testid="spiegel-los">
        <Unlink className="h-4 w-4 mr-1" />{losVraag ? t("Zeker weten? Terug naar spiegelen kan niet.") : t("Maak los van GitHub")}
      </Button>
    </div>
  );
}
